import { batch } from 'react-redux';
import { toast } from 'react-toastify';

import { useAppDispatch } from 'redux/store/store.types';
import { getApiErrorMessage } from 'utils';

import { useCompanyType } from 'modules/company-type';

import {
  useCreateCompanyMutation,
  useCreateCompanyRelationMutation,
  useLazyFetchCompaniesQuery,
  useLazyGetCompanyQuery,
  useRemoveCompanyMutation,
  useRemoveCompanyRelationMutation,
  useUpdateCompanyMutation,
  useUpdateCompanyRelationMutation,
} from '../api/companies.api';
import {
  CompaniesAC,
  setAvailableTypes,
  setCompaniesModal,
  setEditableCompany,
  setSelectedCompany,
} from '../store/companies.slice';
import {
  CreateCompanyPayload,
  CreateCompanyRelationPayload,
  FetchCompaniesPayload,
  GetCompanyPayload,
  ICompaniesModal,
  ICompany,
  ISingleCompany,
  RemoveCompanyPayload,
  RemoveCompanyRelationPayload,
  UpdateCompanyPayload,
  UpdateCompanyRelationPayload,
} from '../types/companies.types';

interface OpenModal {
  modal: ICompaniesModal;
  company: ICompany | null;
}

interface UseCompanies {
  getCompanyHandler: (payload: GetCompanyPayload) => Promise<ISingleCompany | null>;
  createCompanyHandler: (payload: CreateCompanyPayload) => Promise<ISingleCompany | null>;
  updateCompanyHandler: (payload: UpdateCompanyPayload) => Promise<ISingleCompany | null>;
  removeCompanyHandler: (payload: RemoveCompanyPayload) => Promise<boolean>;
  fetchCompaniesHandler: (payload: FetchCompaniesPayload) => Promise<void>;
  openCompaniesModal: (payload: OpenModal) => void;
  fetchCompanyTypesHandler: () => Promise<void>;
  closeCompaniesModal: () => void;
  clearCompaniesState: () => void;

  createCompanyRelationHandler: (payload: CreateCompanyRelationPayload) => Promise<boolean>;
  updateCompanyRelationHandler: (payload: UpdateCompanyRelationPayload) => Promise<boolean>;
  removeCompanyRelationHandler: (payload: RemoveCompanyRelationPayload) => Promise<boolean>;
}

export const useCompanies = (): UseCompanies => {
  const dispatch = useAppDispatch();

  const { fetchCompanyTypesSimpleHandler } = useCompanyType();

  const [getCompanyQuery] = useLazyGetCompanyQuery();
  const [createCompanyQuery] = useCreateCompanyMutation();
  const [updateCompanyQuery] = useUpdateCompanyMutation();
  const [removeCompanyQuery] = useRemoveCompanyMutation();
  const [fetchCompaniesQuery] = useLazyFetchCompaniesQuery();

  const [createCompanyRelationQuery] = useCreateCompanyRelationMutation();
  const [updateCompanyRelationQuery] = useUpdateCompanyRelationMutation();
  const [removeCompanyRelationQuery] = useRemoveCompanyRelationMutation();

  const createCompanyRelationHandler = async (payload: CreateCompanyRelationPayload) => {
    const { data: response, error } = await createCompanyRelationQuery(payload);

    if (error || !response?.data) {
      toast.error(getApiErrorMessage(error) || 'Failed to add company relation!');
      return false;
    }
    toast.success('Company relation added!');
    dispatch(setEditableCompany(response.data));
    return true;
  };

  const updateCompanyRelationHandler = async (payload: UpdateCompanyRelationPayload) => {
    const { data: response, error } = await updateCompanyRelationQuery(payload);
    if (error || !response?.data) {
      toast.error(getApiErrorMessage(error) || 'Failed to update company relation!');
      return false;
    }
    toast.success('Company relation updated!');
    dispatch(setEditableCompany(response.data));
    return true;
  };

  const removeCompanyRelationHandler = async (payload: RemoveCompanyRelationPayload) => {
    const { data: response, error } = await removeCompanyRelationQuery(payload);
    if (error || !response?.data) {
      toast.error(getApiErrorMessage(error) || 'Failed to remove company relation!');
      return false;
    }
    toast.success('Company relation removed!');
    dispatch(setEditableCompany(response.data));
    return true;
  };

  const fetchCompanyTypesHandler = async () => {
    const types = await fetchCompanyTypesSimpleHandler({ page: 1, per_page: 100 });
    dispatch(setAvailableTypes(types));
  };

  const updateCompanyHandler = async (payload: UpdateCompanyPayload) => {
    dispatch(CompaniesAC.toggleLoading(true));
    const { data: response, error } = await updateCompanyQuery(payload);
    dispatch(CompaniesAC.toggleLoading(false));
    if (error || !response?.data) {
      toast.error(getApiErrorMessage(error) || 'Failed to update company!');
      return null;
    }
    return response.data;
  };

  const createCompanyHandler = async (payload: CreateCompanyPayload) => {
    dispatch(CompaniesAC.toggleLoading(true));
    const { data: response, error } = await createCompanyQuery(payload);
    dispatch(CompaniesAC.toggleLoading(false));
    if (error || !response?.data) {
      toast.error(getApiErrorMessage(error) || 'Failed to create company!');
      return null;
    }
    return response.data;
  };

  const getCompanyHandler = async (payload: GetCompanyPayload) => {
    dispatch(CompaniesAC.toggleLoading(true));
    const { data: response, error } = await getCompanyQuery(payload);
    dispatch(CompaniesAC.toggleLoading(false));
    if (error || !response?.data) {
      toast.error(getApiErrorMessage(error) || 'Failed to get company!');
      return null;
    }
    return response.data;
  };

  const fetchCompaniesHandler = async (payload: FetchCompaniesPayload) => {
    dispatch(CompaniesAC.toggleLoading(true));
    const { data: response, error } = await fetchCompaniesQuery(payload);
    dispatch(CompaniesAC.toggleLoading(false));
    if (error || !response?.data) {
      toast.error(getApiErrorMessage(error) || 'Failed to fetch companies!');
      return;
    }
    batch(() => {
      dispatch(CompaniesAC.setItems(response.data));
      dispatch(CompaniesAC.setPaginationMeta(response.meta));
    });
  };

  const removeCompanyHandler = async (payload: RemoveCompanyPayload) => {
    dispatch(CompaniesAC.toggleLoading(true));
    const { error } = await removeCompanyQuery(payload);
    dispatch(CompaniesAC.toggleLoading(false));
    if (error) {
      toast.error(getApiErrorMessage(error) || 'Failed to remove company!');
      return false;
    }
    toast.success('Company successfully removed!');
    return true;
  };

  const openCompaniesModal = ({ modal, company }: OpenModal) => {
    batch(() => {
      dispatch(setSelectedCompany(company));
      dispatch(setCompaniesModal(modal));
    });
  };

  const closeCompaniesModal = () => {
    batch(() => {
      dispatch(setSelectedCompany(null));
      dispatch(setCompaniesModal(null));
    });
  };

  const clearCompaniesState = () => {
    batch(() => {
      dispatch(CompaniesAC.clearItems());
      dispatch(CompaniesAC.clearFilters());
      dispatch(CompaniesAC.clearSorts());
      dispatch(setSelectedCompany(null));
      dispatch(setCompaniesModal(null));
    });
  };

  return {
    getCompanyHandler,
    createCompanyHandler,
    updateCompanyHandler,
    removeCompanyHandler,
    fetchCompaniesHandler,
    openCompaniesModal,
    fetchCompanyTypesHandler,
    closeCompaniesModal,
    clearCompaniesState,
    createCompanyRelationHandler,
    updateCompanyRelationHandler,
    removeCompanyRelationHandler,
  };
};
